/**
 * KonvaCanvas - Core canvas component using Konva.js
 *
 * Handles:
 * - Stage setup with fill-container scaling
 * - Layer rendering with proper coordinate transform
 * - Shape rendering (rects and graphics)
 * - Selection and Transformer
 * - Snap guides visualization
 *
 * Ported from Tauri desktop app to SaaS
 */
import { useRef, useEffect, useMemo, useCallback, useState, RefObject } from 'react';
import { Stage, Layer, Rect, Transformer, Line, Group } from 'react-konva';
import Konva from 'konva';
import type { TemplateShape, CustomizationField } from '@jigflow/shared';
import { calculateSnap, SnapGuide } from '@jigflow/shared/utils/snapUtils';
import { CursorMode } from '../useTemplateStore';
import { RectShape } from './RectShape';
import { GraphicShape } from './GraphicShape';
import { RasterShape } from './RasterShape';

// ============================================================
// Inline useContainerSize hook
// TODO: Port to @jigflow/shared/hooks and import from there
// ============================================================

interface ContainerSize {
    width: number;
    height: number;
}

function useContainerSize(containerRef: RefObject<HTMLDivElement | null>): ContainerSize {
    const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateSize = () => {
            setSize({
                width: container.clientWidth,
                height: container.clientHeight
            });
        };

        // Initial size
        updateSize();

        // Watch for resizes
        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [containerRef]);

    return size;
}

// ============================================================
// KonvaCanvas Component
// ============================================================

interface KonvaCanvasProps {
    shapes: TemplateShape[];
    selectedId: string | null;
    workAreaWidth: number;
    workAreaHeight: number;
    fields: CustomizationField[];
    snapEnabled: boolean;
    cursorMode: CursorMode;
    zoom: number;
    gridEnabled?: boolean;
    onSelect: (id: string | null) => void;
    onShapeChange: (id: string, updates: Partial<TemplateShape>) => void;
    onDrawRect?: (x: number, y: number, width: number, height: number) => void;
    onZoomChange?: (zoom: number) => void;
    onPanReset?: () => void;
    onScaleOffsetChange?: (scale: number, offsetX: number, offsetY: number, containerWidth: number, containerHeight: number) => void;
}

interface DrawingRect {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
}

export function KonvaCanvas({
    shapes,
    selectedId,
    workAreaWidth,
    workAreaHeight,
    fields,
    snapEnabled,
    cursorMode,
    zoom,
    gridEnabled = false,
    onSelect,
    onShapeChange,
    onDrawRect,
    onZoomChange,
    onPanReset: _onPanReset,
    onScaleOffsetChange
}: KonvaCanvasProps) {
    void _onPanReset; // Reserved for future use
    const containerRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<Konva.Stage>(null);
    const shapesLayerRef = useRef<Konva.Layer>(null);  // Layer ref for getRelativePointerPosition
    const { width: containerWidth, height: containerHeight } = useContainerSize(containerRef);
    const transformerRef = useRef<Konva.Transformer>(null);
    const shapeRefs = useRef<Map<string, Konva.Node>>(new Map());
    const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);
    const [drawingRect, setDrawingRect] = useState<DrawingRect | null>(null);
    const isDrawing = useRef(false);

    // Pan state
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const isPanning = useRef(false);
    const lastPanPos = useRef({ x: 0, y: 0 });
    const [spacePressed, setSpacePressed] = useState(false);

    // Calculate base scale to fit work area in container with padding
    const padding = 32; // 16px on each side
    const baseScale = useMemo(() => {
        if (containerWidth <= padding || containerHeight <= padding) return 1;
        const scaleX = (containerWidth - padding) / workAreaWidth;
        const scaleY = (containerHeight - padding) / workAreaHeight;
        return Math.min(scaleX, scaleY);
    }, [containerWidth, containerHeight, workAreaWidth, workAreaHeight]);

    // Apply zoom to base scale
    const scale = baseScale * zoom;

    // Center the work area in the container (with pan offset)
    const offsetX = (containerWidth - workAreaWidth * scale) / 2 + panOffset.x;
    const offsetY = (containerHeight - workAreaHeight * scale) / 2 + panOffset.y;

    // Reset pan when zoom resets to 100%
    useEffect(() => {
        if (zoom === 1) {
            setPanOffset({ x: 0, y: 0 });
        }
    }, [zoom]);

    // Report scale/offset changes to parent for rulers
    useEffect(() => {
        if (onScaleOffsetChange && containerWidth > 0 && containerHeight > 0) {
            onScaleOffsetChange(scale, offsetX, offsetY, containerWidth, containerHeight);
        }
    }, [scale, offsetX, offsetY, containerWidth, containerHeight, onScaleOffsetChange]);

    // Calculate zoom-adaptive grid spacing (in mm)
    const getGridSpacing = useCallback((zoomLevel: number): number => {
        // Adjust spacing based on zoom level for readability
        if (zoomLevel < 0.5) return 50;     // Very zoomed out: 50mm grid
        if (zoomLevel < 1) return 25;        // Zoomed out: 25mm grid
        if (zoomLevel < 2) return 10;        // Normal: 10mm grid
        if (zoomLevel < 4) return 5;         // Zoomed in: 5mm grid
        return 2;                             // Very zoomed in: 2mm grid
    }, []);

    // Generate grid lines
    const gridLines = useMemo(() => {
        if (!gridEnabled) return { vertical: [], horizontal: [] };

        const spacing = getGridSpacing(zoom);
        const vertical: number[] = [];
        const horizontal: number[] = [];

        // Vertical lines
        for (let x = spacing; x < workAreaWidth; x += spacing) {
            vertical.push(x);
        }

        // Horizontal lines
        for (let y = spacing; y < workAreaHeight; y += spacing) {
            horizontal.push(y);
        }

        return { vertical, horizontal };
    }, [gridEnabled, zoom, workAreaWidth, workAreaHeight, getGridSpacing]);

    // Keyboard listener for spacebar (pan mode)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code === 'Space' && !e.repeat) {
                e.preventDefault();
                setSpacePressed(true);
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                setSpacePressed(false);
                isPanning.current = false;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // Register shape ref and update transformer if needed
    const registerRef = useCallback((id: string, node: Konva.Node | null) => {
        if (node) {
            shapeRefs.current.set(id, node);
        } else {
            shapeRefs.current.delete(id);
        }

        // If this is the selected shape, attach transformer immediately
        if (id === selectedId && transformerRef.current) {
            if (node) {
                transformerRef.current.nodes([node]);
            } else {
                transformerRef.current.nodes([]);
            }
            transformerRef.current.getLayer()?.batchDraw();
        }
    }, [selectedId]);

    // Update transformer when selection changes
    useEffect(() => {
        const transformer = transformerRef.current;
        if (!transformer) return;

        if (selectedId) {
            const node = shapeRefs.current.get(selectedId);
            if (node) {
                transformer.nodes([node]);
            } else {
                transformer.nodes([]);
            }
        } else {
            transformer.nodes([]);
        }
        transformer.getLayer()?.batchDraw();
    }, [selectedId, shapes]); // Re-run when shapes change too

    // Helper to check if a shape is a border rect
    const isBorderRect = useCallback((shape: TemplateShape) => {
        return shape.type === 'rect' &&
            Math.abs(shape.width - workAreaWidth) < 1 &&
            Math.abs(shape.height - workAreaHeight) < 1 &&
            Math.abs(shape.x) < 1 && Math.abs(shape.y) < 1;
    }, [workAreaWidth, workAreaHeight]);

    // Sort shapes for rendering order (bottom to top):
    // 1. Border rects (bottom layer - workspace outline)
    // 2. Non-border rects (field zones)
    // 3. Graphics (imported SVGs, text) on top
    // This ensures graphics are always clickable above rect zones
    const sortedShapes = useMemo(() => {
        return [...shapes].sort((a, b) => {
            const aIsBorder = isBorderRect(a);
            const bIsBorder = isBorderRect(b);

            // Border rects first (very bottom)
            if (aIsBorder && !bIsBorder) return -1;
            if (!aIsBorder && bIsBorder) return 1;

            // Non-border rects next (above border, below graphics)
            const aIsRect = a.type === 'rect';
            const bIsRect = b.type === 'rect';
            if (aIsRect && !bIsRect) return -1;  // rects before graphics
            if (!aIsRect && bIsRect) return 1;   // graphics after rects

            // Within same type, sort by orderIndex
            return a.orderIndex - b.orderIndex;
        });
    }, [shapes, isBorderRect]);

    // Get field for a shape
    const getFieldForShape = useCallback((shape: TemplateShape): CustomizationField | undefined => {
        if (!shape.fieldId) return undefined;
        return fields.find(f => f.id === shape.fieldId);
    }, [fields]);

    // Convert screen coordinates to work area coordinates
    const screenToWorkArea = useCallback((screenX: number, screenY: number) => {
        const x = (screenX - offsetX) / scale;
        const y = (screenY - offsetY) / scale;
        // Allow drawing outside workarea - user is responsible for placement
        return { x, y };
    }, [offsetX, offsetY, scale]);

    // Check if a point is within a shape's bounding box
    const isPointInShape = useCallback((x: number, y: number, shape: TemplateShape): boolean => {
        // Simple bounding box check (ignoring rotation for now)
        return x >= shape.x && x <= shape.x + shape.width &&
               y >= shape.y && y <= shape.y + shape.height;
    }, []);

    // Handle shape click - just pass the clicked shape ID, actual selection logic is in Stage click
    // This is used to identify WHICH shape was clicked (for non-selection actions in future)
    const handleShapeClick = useCallback((clickedShapeId: string) => {
        // Selection is now handled in handleStageClick
        // This callback exists for shapes that need to know they were clicked
        void clickedShapeId;
    }, []);

    // Handle mouse down for drawing or panning
    const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
        // Middle mouse (button 1) OR (spacebar + left click) = pan
        if (e.evt.button === 1 || (spacePressed && e.evt.button === 0)) {
            e.evt.preventDefault();
            isPanning.current = true;
            lastPanPos.current = { x: e.evt.clientX, y: e.evt.clientY };
            return;
        }

        // In move mode with a selected shape, check if click is within selected shape's bounds
        // If so, manually start a drag on the selected shape (allows dragging through overlapping shapes)
        if (cursorMode === 'move' && selectedId && e.evt.button === 0) {
            // Don't start drag when clicking on Transformer anchors (resize handles)
            // Otherwise both drag and transform fire simultaneously
            if (transformerRef.current) {
                let ancestor: Konva.Node | null = e.target;
                while (ancestor) {
                    if (ancestor === transformerRef.current) return;
                    ancestor = ancestor.getParent();
                }
            }

            const shapesLayer = shapesLayerRef.current;
            if (shapesLayer) {
                const workPos = shapesLayer.getRelativePointerPosition();
                if (workPos) {
                    const selectedShape = shapes.find(s => s.id === selectedId);
                    if (selectedShape && isPointInShape(workPos.x, workPos.y, selectedShape)) {
                        // Get the selected shape's node and manually start drag
                        const selectedNode = shapeRefs.current.get(selectedId);
                        if (selectedNode) {
                            // Start drag on the selected node
                            selectedNode.startDrag();
                            return;
                        }
                    }
                }
            }
        }

        if (cursorMode !== 'draw') return;

        const stage = e.target.getStage();
        if (!stage) return;

        const pos = stage.getPointerPosition();
        if (!pos) return;

        const workPos = screenToWorkArea(pos.x, pos.y);
        isDrawing.current = true;
        setDrawingRect({
            startX: workPos.x,
            startY: workPos.y,
            currentX: workPos.x,
            currentY: workPos.y
        });
    }, [cursorMode, screenToWorkArea, spacePressed, selectedId, shapes, isPointInShape]);

    // Handle mouse move for panning or drawing
    const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
        // Handle panning
        if (isPanning.current) {
            const dx = e.evt.clientX - lastPanPos.current.x;
            const dy = e.evt.clientY - lastPanPos.current.y;
            setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
            lastPanPos.current = { x: e.evt.clientX, y: e.evt.clientY };
            return;
        }

        // Handle drawing
        if (!isDrawing.current || !drawingRect) return;

        const stage = e.target.getStage();
        if (!stage) return;

        const pos = stage.getPointerPosition();
        if (!pos) return;

        const workPos = screenToWorkArea(pos.x, pos.y);
        setDrawingRect(prev => prev ? { ...prev, currentX: workPos.x, currentY: workPos.y } : null);
    }, [drawingRect, screenToWorkArea]);

    // Handle mouse up for panning or drawing
    const handleMouseUp = useCallback(() => {
        // End panning
        if (isPanning.current) {
            isPanning.current = false;
            return;
        }

        // End drawing
        if (!isDrawing.current || !drawingRect) return;

        isDrawing.current = false;

        // Calculate rect bounds (handle negative width/height from drawing direction)
        const x = Math.min(drawingRect.startX, drawingRect.currentX);
        const y = Math.min(drawingRect.startY, drawingRect.currentY);
        const width = Math.abs(drawingRect.currentX - drawingRect.startX);
        const height = Math.abs(drawingRect.currentY - drawingRect.startY);

        setDrawingRect(null);

        // Call onDrawRect if we have a valid size
        if (onDrawRect && width > 0 && height > 0) {
            onDrawRect(x, y, width, height);
        }
    }, [drawingRect, onDrawRect]);

    // Handle click on stage - ALL selection logic happens here
    const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        // In draw mode, don't handle clicks as selection (drawing is handled separately)
        if (cursorMode === 'draw') return;

        const target = e.target;
        const stage = stageRef.current;
        const shapesLayer = shapesLayerRef.current;
        if (!stage || !shapesLayer) return;

        // If clicking on empty area (stage or workarea background), deselect
        if (target === stage || target.name() === 'workarea-bg') {
            onSelect(null);
            return;
        }

        // First, find which shape was clicked by traversing up from target
        let clickedShapeId: string | null = null;
        let node: Konva.Node | null = target;
        while (node && node !== stage) {
            for (const [id, ref] of shapeRefs.current.entries()) {
                if (ref === node) {
                    clickedShapeId = id;
                    break;
                }
            }
            if (clickedShapeId) break;
            node = node.parent;
        }

        // If the clicked shape is the selected shape, keep it selected
        if (clickedShapeId === selectedId) {
            return;
        }

        // KEY FIX: Get pointer position relative to the Layer (work area coords)
        // This uses Konva's internal transform matrix - more reliable than manual calculation
        const workPos = shapesLayer.getRelativePointerPosition();

        // If there's a selected shape, check if click is within its bounds
        // This allows clicking "through" overlapping shapes to the selected one
        if (selectedId && workPos) {
            const selectedShape = shapes.find(s => s.id === selectedId);
            if (selectedShape && isPointInShape(workPos.x, workPos.y, selectedShape)) {
                // Click is within selected shape's bounds - keep selection
                // even though another shape (on top) was technically clicked
                return;
            }
        }

        // If no shape was clicked and click is outside selected shape, deselect
        if (!clickedShapeId) {
            onSelect(null);
            return;
        }

        // Select the clicked shape
        onSelect(clickedShapeId);
    }, [cursorMode, selectedId, shapes, isPointInShape, onSelect]);

    // Handle mouse wheel for zooming
    const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
        e.evt.preventDefault();
        if (!onZoomChange) return;

        const scaleBy = 1.1;
        const newZoom = e.evt.deltaY < 0
            ? Math.min(zoom * scaleBy, 5) // Max 500%
            : Math.max(zoom / scaleBy, 0.25); // Min 25%

        onZoomChange(newZoom);
    }, [zoom, onZoomChange]);

    // Handle shape change from child components
    const handleShapeChange = useCallback((id: string, updates: Partial<TemplateShape>) => {
        onShapeChange(id, updates);
    }, [onShapeChange]);

    // Handle drag move with snapping
    const handleDragMove = useCallback((
        id: string,
        x: number,
        y: number,
        width: number,
        height: number,
        node: Konva.Node
    ) => {
        if (!snapEnabled) {
            setSnapGuides([]);
            return;
        }

        const snapResult = calculateSnap(
            { x, y, width, height, id },
            shapes,
            workAreaWidth,
            workAreaHeight,
            5 // threshold in mm
        );

        // Update node position if snapped
        if (snapResult.x !== x || snapResult.y !== y) {
            node.x(snapResult.x);
            node.y(snapResult.y);
        }

        setSnapGuides(snapResult.guides);
    }, [snapEnabled, shapes, workAreaWidth, workAreaHeight]);

    // Clear guides on drag end
    const handleDragEnd = useCallback(() => {
        setSnapGuides([]);
    }, []);

    // Cursor style based on mode and pan state
    const cursorStyle = useMemo(() => {
        if (spacePressed) return 'grab';
        switch (cursorMode) {
            case 'draw': return 'crosshair';
            case 'move': return 'default';
            case 'select': return 'default';
            default: return 'default';
        }
    }, [cursorMode, spacePressed]);

    // Calculate drawing preview rect (handle negative dimensions)
    const drawPreview = useMemo(() => {
        if (!drawingRect) return null;
        return {
            x: Math.min(drawingRect.startX, drawingRect.currentX),
            y: Math.min(drawingRect.startY, drawingRect.currentY),
            width: Math.abs(drawingRect.currentX - drawingRect.startX),
            height: Math.abs(drawingRect.currentY - drawingRect.startY)
        };
    }, [drawingRect]);

    return (
        <div
            ref={containerRef}
            className="flex-1 bg-slate-200 dark:bg-slate-950 overflow-hidden"
            style={{ minWidth: 0, minHeight: 0, cursor: cursorStyle }}
        >
            {containerWidth > 0 && containerHeight > 0 && (
                <Stage
                    ref={stageRef}
                    width={containerWidth}
                    height={containerHeight}
                    onClick={handleStageClick}
                    onTap={handleStageClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                >
                    {/* Layer 1: Work area background + RectShapes */}
                    <Layer ref={shapesLayerRef} x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
                        {/* Work area background */}
                        <Rect
                            name="workarea-bg"
                            x={0}
                            y={0}
                            width={workAreaWidth}
                            height={workAreaHeight}
                            fill="white"
                            stroke="#64748b"
                            strokeWidth={2 / scale}
                            shadowColor="black"
                            shadowBlur={10 / scale}
                            shadowOpacity={0.15}
                            shadowOffset={{ x: 0, y: 4 / scale }}
                        />

                        {/* Grid overlay */}
                        {gridEnabled && (
                            <Group listening={false}>
                                {/* Vertical lines */}
                                {gridLines.vertical.map((x) => (
                                    <Line
                                        key={`grid-v-${x}`}
                                        points={[x, 0, x, workAreaHeight]}
                                        stroke="#e2e8f0"
                                        strokeWidth={0.5 / scale}
                                    />
                                ))}
                                {/* Horizontal lines */}
                                {gridLines.horizontal.map((y) => (
                                    <Line
                                        key={`grid-h-${y}`}
                                        points={[0, y, workAreaWidth, y]}
                                        stroke="#e2e8f0"
                                        strokeWidth={0.5 / scale}
                                    />
                                ))}
                            </Group>
                        )}

                        {/* Render RectShapes (field zones, border rects) */}
                        {sortedShapes.filter(s => s.type !== 'graphic' && s.type !== 'raster').map((shape) => (
                            <RectShape
                                key={shape.id}
                                shape={shape}
                                field={getFieldForShape(shape)}
                                isSelected={selectedId === shape.id}
                                isLocked={isBorderRect(shape)}
                                workAreaWidth={workAreaWidth}
                                workAreaHeight={workAreaHeight}
                                scale={scale}
                                cursorMode={cursorMode}
                                onSelect={() => onSelect(shape.id)}
                                onChange={(updates: Partial<TemplateShape>) => handleShapeChange(shape.id, updates)}
                                onDragMove={handleDragMove}
                                onDragEnd={handleDragEnd}
                                registerRef={registerRef}
                            />
                        ))}
                    </Layer>

                    {/* Layer 2: GraphicShapes and RasterShapes - isolated canvas to prevent bounding box clipping */}
                    <Layer x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
                        {/* Render RasterShapes (raster images for engraving) */}
                        {sortedShapes.filter(s => s.type === 'raster').map((shape) => (
                            <RasterShape
                                key={shape.id}
                                shape={shape}
                                isSelected={selectedId === shape.id}
                                workAreaWidth={workAreaWidth}
                                workAreaHeight={workAreaHeight}
                                scale={scale}
                                cursorMode={cursorMode}
                                onSelect={() => onSelect(shape.id)}
                                onChange={(updates: Partial<TemplateShape>) => handleShapeChange(shape.id, updates)}
                                onDragMove={handleDragMove}
                                onDragEnd={handleDragEnd}
                                registerRef={registerRef}
                            />
                        ))}
                        {/* Render GraphicShapes (imported SVGs) */}
                        {sortedShapes.filter(s => s.type === 'graphic').map((shape) => (
                            <GraphicShape
                                key={shape.id}
                                shape={shape}
                                isSelected={selectedId === shape.id}
                                workAreaWidth={workAreaWidth}
                                workAreaHeight={workAreaHeight}
                                scale={scale}
                                cursorMode={cursorMode}
                                onSelect={() => onSelect(shape.id)}
                                onChange={(updates: Partial<TemplateShape>) => handleShapeChange(shape.id, updates)}
                                onDragMove={handleDragMove}
                                onDragEnd={handleDragEnd}
                                registerRef={registerRef}
                            />
                        ))}
                    </Layer>

                    {/* Layer 3: UI elements (Transformer, guides, preview) - always on top */}
                    <Layer x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
                        {/* Drawing preview rect */}
                        {drawPreview && (
                            <Rect
                                x={drawPreview.x}
                                y={drawPreview.y}
                                width={drawPreview.width}
                                height={drawPreview.height}
                                fill="rgba(59, 130, 246, 0.1)"
                                stroke="#3b82f6"
                                strokeWidth={2 / scale}
                                dash={[4 / scale, 4 / scale]}
                                listening={false}
                            />
                        )}

                        {/* Transformer for resize/rotate */}
                        <Transformer
                            ref={transformerRef}
                            rotateEnabled={cursorMode === 'move'}
                            rotateAnchorOffset={30 / scale}
                            anchorStyleFunc={(anchor) => {
                                // Style the rotate anchor to make it more visible and bigger
                                if (anchor.hasName('rotater')) {
                                    anchor.fill('#3b82f6');
                                    anchor.stroke('#1d4ed8');
                                    anchor.strokeWidth(2 / scale);
                                    // Make rotate handle 50% bigger than normal anchors
                                    const baseSize = Math.max(12 / scale, 6);
                                    anchor.width(baseSize * 1.5);
                                    anchor.height(baseSize * 1.5);
                                    anchor.offsetX(baseSize * 0.75);
                                    anchor.offsetY(baseSize * 0.75);
                                }
                            }}
                            keepRatio={false}
                            anchorSize={Math.max(12 / scale, 6)}
                            anchorCornerRadius={Math.max(3 / scale, 2)}
                            borderStroke="#3b82f6"
                            borderStrokeWidth={Math.max(2 / scale, 1)}
                            anchorStroke="#3b82f6"
                            anchorFill="white"
                            anchorStrokeWidth={Math.max(1.5 / scale, 1)}
                            enabledAnchors={cursorMode === 'move' ? [
                                'top-left', 'top-center', 'top-right',
                                'middle-left', 'middle-right',
                                'bottom-left', 'bottom-center', 'bottom-right'
                            ] : []}
                            boundBoxFunc={(_oldBox, newBox) => {
                                const minSize = 5;
                                if (newBox.width < minSize) newBox.width = minSize;
                                if (newBox.height < minSize) newBox.height = minSize;
                                return newBox;
                            }}
                        />

                        {/* Snap guide lines */}
                        {snapGuides.map((guide, index) => (
                            guide.type === 'vertical' ? (
                                <Line
                                    key={`guide-${index}`}
                                    points={[guide.position, 0, guide.position, workAreaHeight]}
                                    stroke="#3b82f6"
                                    strokeWidth={1 / scale}
                                    dash={[4 / scale, 4 / scale]}
                                    listening={false}
                                />
                            ) : (
                                <Line
                                    key={`guide-${index}`}
                                    points={[0, guide.position, workAreaWidth, guide.position]}
                                    stroke="#3b82f6"
                                    strokeWidth={1 / scale}
                                    dash={[4 / scale, 4 / scale]}
                                    listening={false}
                                />
                            )
                        ))}
                    </Layer>
                </Stage>
            )}
        </div>
    );
}
